/**
 * Rank sets: one design, many files.
 *
 * A group owner with eight ranks does not want eight shirts. They want one
 * shirt and eight badges, and the moment the shirt changes they want all eight
 * to change with it. That is the pain this exists for, and it is the reason a
 * variant is a **sparse override on top of the base project** rather than a
 * copy: nothing is duplicated, so there is nothing to keep in step.
 *
 * `Variant` and `VariantOverride` were already in the format, and already
 * round-trip through pack/unpack with a `MAX_VARIANTS` cap. What was missing is
 * everything that reads them.
 *
 * ## What an override may say
 *
 * Three things, and they are deliberately not "a whole layer":
 *
 *   `visible`   — show this layer in this variant, hide it in the others. The
 *                 whole rank set falls out of this one field: put every badge
 *                 in the project, and let each variant show its own.
 *   `placement` — a partial patch, so a badge can sit slightly differently for
 *                 one rank without restating where it is for all of them.
 *   `pixelsKey` — swap the artwork itself. Serialisation already supports it;
 *                 nothing in the editor writes one yet.
 *
 * Anything a variant does not mention is inherited unchanged. That is the whole
 * mechanism, and it is why editing the shirt updates every rank at once.
 */
import { type FacePlacement, MAX_VARIANTS, type Variant } from "../../shared/format";

/**
 * The minimum a layer must expose to be varied.
 *
 * Structural rather than the concrete `Layer` or `RuntimeLayer`, because both
 * have to go through this: the editor varies runtime layers to show a preview,
 * and the exporter varies saved layers to write files. One rule, both callers —
 * the same reason `landsHere` is shared between the paint and its preview.
 */
export interface Varyable {
  id: string;
  visible: boolean;
  placement?: FacePlacement;
}

/**
 * The base layers as this variant sees them.
 *
 * Returns new objects; the originals are untouched. A variant is a *view* of
 * the project, and one that mutated the base would make "editing the shirt
 * updates all eight ranks" impossible in the most direct way possible — there
 * would be no base left to edit.
 *
 * `null` means the base itself, which is what the editor shows when no variant
 * is selected, and what the exporter writes when a project has none.
 */
export function applyVariant<T extends Varyable>(
  layers: readonly T[],
  variant?: Variant | null,
): T[] {
  if (!variant) return layers.map((l) => ({ ...l }));
  return layers.map((layer) => {
    const over = variant.overrides[layer.id];
    if (!over) return { ...layer };
    const next: T = { ...layer };
    if (over.visible !== undefined) next.visible = over.visible;
    if (over.placement && layer.placement) {
      // Merged, not replaced: an override that only nudges `x` must not drop
      // the scale and rotation the base layer carries.
      next.placement = { ...layer.placement, ...over.placement };
    }
    return next;
  });
}

/** Whether this variant says anything at all about a layer. */
export function overridesLayer(variant: Variant, layerId: string): boolean {
  return Object.hasOwn(variant.overrides, layerId);
}

/**
 * Show exactly one of a set of layers, and hide the rest.
 *
 * The rank-set shape, as one call. Building it by hand means remembering to
 * write `visible: false` for the seven badges you are *not* showing — and an
 * override that only says "show mine" inherits the others' base visibility,
 * which is every badge stacked on every rank.
 */
export function soloOverrides(layerIds: readonly string[], showId: string): Variant["overrides"] {
  const out: Variant["overrides"] = {};
  for (const id of layerIds) out[id] = { visible: id === showId };
  return out;
}

/**
 * A name no other variant is using.
 *
 * Names become filenames, and two files called `crew-corporal.png` means one of
 * them silently replaced the other in the user's downloads folder. Numbered
 * rather than rejected, because renaming eight ranks by hand to satisfy a
 * validation message is worse than being handed "Corporal 2".
 */
export function uniqueVariantName(existing: readonly string[], proposed: string): string {
  const taken = new Set(existing.map((n) => n.trim().toLowerCase()));
  const base = proposed.trim() || "Variant";
  if (!taken.has(base.toLowerCase())) return base;
  for (let n = 2; n < 1000; n++) {
    const candidate = `${base} ${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return `${base} ${Date.now()}`;
}

/** Whether another variant can be added. See `MAX_VARIANTS` for why there is a cap. */
export function canAddVariant(variants: readonly Variant[] | undefined): boolean {
  return (variants?.length ?? 0) < MAX_VARIANTS;
}

/**
 * The filename for one variant of a design.
 *
 * The variant's name is folded into the slug rather than appended raw, so
 * "Sergeant Major" cannot produce a filename with a space in it — and the
 * canvas size stays on the end for the same reason `filenameFor` keeps it:
 * wrong dimensions are a top cause of rejected uploads.
 *
 * Deliberately takes the base filename apart rather than re-deriving it, so
 * there is one definition of how a design is named and this only inserts.
 */
export function variantFilename(baseFilename: string, variantName: string): string {
  const slug =
    variantName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "variant";
  // `name-shirt-585x559.png` -> `name-shirt-corporal-585x559.png`
  const match = baseFilename.match(/^(.*?)(-\d+x\d+)?(\.png)$/i);
  if (!match) return `${baseFilename}-${slug}`;
  const [, stem, size = "", ext] = match;
  return `${stem}-${slug}${size}${ext}`;
}
